import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { OrderService, ListCustomerOrdersRequest, ListCustomerOrdersResponse, Order, OrderStatus, Customer } from '../api/api_pb';
import { useClient } from '../connect_api';
import RelativeTime from '../components/RelativeTime';
import { toast } from 'react-toastify';
import CurrencyFormatter from '../utils/currencyFormatter';
import { Form, Modal, Col, Row, Button } from 'react-bootstrap';
import { useGlobalContext } from '../GlobalContext';
import { formatAuthMethod } from '../utils/authMethodUtils';
import MobileNumberActions from '../components/MobileNumberActions';
import SendSMSModal from '../components/SendSMSModal';
import NayaxCaptureActions from '../components/NayaxCaptureActions';
import { isRefundable } from '../utils/nayaxUtils';
import {
    AlertCircle,
    CheckCircle,
    Clock,
    FileText,
    RotateCcw,
    Truck,
    XCircle,
} from 'lucide-react';

const ShowLease: React.FC = () => {
    const { customerUuid } = useParams();
    const { findCustomerByUuid } = useGlobalContext();

    const orderClient = useClient(OrderService);
    const [customer, setCustomer] = useState<Customer | undefined>(
        () => findCustomerByUuid(customerUuid!)
    );
    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [isRefunding, setIsRefunding] = useState<boolean>(false);
    const [refundNote, setRefundNote] = useState<string>('');
    const [smsTarget, setSmsTarget] = useState<{ uuid: string; mobileNumber: string } | null>(null);

    const fetchOrders = useCallback(async () => {
        if (!customerUuid) return;

        setIsLoading(true);
        try {
            const request = { customerUuid } as ListCustomerOrdersRequest;
            const response = await orderClient.listCustomerOrders(request);
            const { orders: fetchedOrders } = response as ListCustomerOrdersResponse;
            setOrders(fetchedOrders);

            if (!customer && fetchedOrders.length > 0 && fetchedOrders[0].customer) {
                setCustomer(fetchedOrders[0].customer);
            }
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            toast.dismiss();
            toast.error('Failed to fetch orders');
        } finally {
            setIsLoading(false);
        }
    }, [customerUuid, orderClient, customer]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleRefundClick = (order: Order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const handleRefundModalClose = () => {
        setShowModal(false);
        setRefundNote('');
        setSelectedOrder(null);
    };

    const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRefundNote(e.target.value);
    };

    const confirmRefund = () => {
        if (!selectedOrder || isRefunding) return;
        setIsRefunding(true);

        orderClient.refund({
            orderUuid: selectedOrder.uuid,
            refundNote,
        }).then(() => {
            toast.dismiss();
            toast.success('The order has been successfully refunded');
            fetchOrders();
        }).catch((err: any) => {
            toast.dismiss();
            toast.error('Failed to refund order');
            console.log('error', err);
        }).finally(() => {
            setIsRefunding(false);
            setSelectedOrder(null);
            setShowModal(false);
        });
    };

    const getStatusColor = (status: number) => {
        const statusName = OrderStatus[status];
        switch (statusName) {
            case 'COMPLETED':
            case 'VIRTUAL_FULFILLED':
                return 'bg-green-100 text-green-800';
            case 'DISPENSED':
                return 'bg-blue-100 text-blue-800';
            case 'PENDING_PAYMENT':
                return 'bg-yellow-100 text-yellow-800';
            case 'CANCELED':
            case 'PENDING_PAYMENT_TIMEOUT':
            case 'VIRTUAL_FULFILLMENT_FAILURE':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status: number) => {
        const statusName = OrderStatus[status];
        switch (statusName) {
            case 'COMPLETED':
            case 'VIRTUAL_FULFILLED':
                return <CheckCircle className="w-4 h-4" />;
            case 'DISPENSED':
                return <Truck className="w-4 h-4" />;
            case 'PENDING_PAYMENT':
                return <Clock className="w-4 h-4" />;
            case 'CANCELED':
            case 'PENDING_PAYMENT_TIMEOUT':
            case 'VIRTUAL_FULFILLMENT_FAILURE':
                return <XCircle className="w-4 h-4" />;
            default:
                return <AlertCircle className="w-4 h-4" />;
        }
    };

    const OrderListItem = ({ order }: { order: Order }) => (
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-100">
            <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {OrderStatus[order.status]}
                        </span>
                        <span className="font-mono text-xs text-gray-500 truncate">{order.uuid}</span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600">Product</p>
                            <p className="font-medium">{order.productName || 'N/A'}</p>
                            {order.trackableItemName && <p className="text-xs text-gray-500">Trackable: {order.trackableItemName}</p>}
                        </div>
                        <div>
                            <p className="text-gray-600">Price</p>
                            <p className="font-bold"><CurrencyFormatter priceInCents={order.priceInCents} /></p>
                        </div>
                        <div>
                            <p className="text-gray-600">Payment</p>
                            {order.paymentInformation ? (
                                <p className="font-medium">{order.paymentInformation.cardBrand} •••• {order.paymentInformation.last4}</p>
                            ) : (
                                <p className="text-gray-500">N/A</p>
                            )}
                        </div>
                        <div>
                            <p className="text-gray-600">Machine/Location</p>
                            <p className="font-medium">{order.machineName || 'N/A'}</p>
                            <p className="text-xs text-gray-500">Door: {order.doorIdDisplay || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Created</p>
                            <p className="text-gray-900">{order.createdAt ? <RelativeTime timestamp={order.createdAt} /> : 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Auth Method</p>
                            <p className="font-medium">{formatAuthMethod(order.authMethod, order.venueAccessCode)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Returned</p>
                            <p className="text-gray-900">{order.returnedAt ? <RelativeTime timestamp={order.returnedAt} /> : 'Not returned'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Paid</p>
                            <p className="text-gray-900">{order.paidAt ? <RelativeTime timestamp={order.paidAt} /> : 'N/A'}</p>
                        </div>
                    </div>
                </div>

                {order.isNayaxOrder && (
                    <div className="flex-shrink-0">
                        <NayaxCaptureActions order={order} onActionComplete={fetchOrders} />
                    </div>
                )}

                <div className="flex-shrink-0">
                    {isRefundable(order) && (
                        <button
                            onClick={() => handleRefundClick(order)}
                            disabled={isRefunding}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Refund"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </button>
                    )}
                    {order.refundStatus?.refunded && (
                        <div className="p-2 text-red-600">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-6 animate-fade-in">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer</h2>
                {customer ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600">UUID</p>
                            <p className="font-mono text-xs break-all text-gray-900">{customer.uuid}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Mobile</p>
                            <MobileNumberActions
                                mobileNumber={customer.mobileNumber}
                                customerUuid={customer.uuid}
                                onSMSClick={() => setSmsTarget({ uuid: customer.uuid, mobileNumber: customer.mobileNumber })}
                            />
                        </div>
                        <div>
                            <p className="text-gray-600">Created</p>
                            <p className="text-gray-900">{customer.createdAt ? <RelativeTime timestamp={customer.createdAt} /> : 'N/A'}</p>
                        </div>
                    </div>
                ) : (
                    <p className="text-gray-600">No customer data available</p>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Leases</h1>
                        <p className="text-sm text-gray-600">Customer-specific lease orders</p>
                    </div>
                    <div className="text-sm text-gray-600">{orders.length} orders</div>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                            <p className="text-gray-600">Loading orders...</p>
                        </div>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-10">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No Orders Found</h3>
                        <p className="text-gray-600">No orders found for this customer.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {orders.map((order) => (
                            <OrderListItem key={order.uuid} order={order} />
                        ))}
                    </div>
                )}
            </div>

            <SendSMSModal
                isOpen={smsTarget !== null}
                onClose={() => setSmsTarget(null)}
                customerUuid={smsTarget?.uuid ?? ''}
                mobileNumber={smsTarget?.mobileNumber ?? ''}
            />

            <Modal show={showModal} onHide={handleRefundModalClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Refund</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group as={Row} controlId="formOrderUuid" className="mb-3">
                        <Form.Label column sm={4}>
                            Order Uuid
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} placeholder="" value={selectedOrder?.uuid || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formCustomerMobile" className="mb-3">
                        <Form.Label column sm={4}>
                            Customer
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} placeholder="" value={selectedOrder?.customer?.mobileNumber || ''} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="formRefundPrice" className="mb-3">
                        <Form.Label column sm={4}>
                            Refund Price In Cents
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled={true} placeholder="" value={selectedOrder?.priceInCents || ''} />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} controlId="formRefundNote" className="mb-3">
                        <Form.Label column sm={4}>
                            Refund Note
                        </Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" placeholder="Please enter the refund note" onChange={handleNoteChange} />
                        </Col>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleRefundModalClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmRefund}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShowLease;
